// TODO(stripe-reimpl): this file has legacy Stripe billing code, disabled while we're betaAccess-only. Rebuild on the new billing system.
import React, { useState, useContext, useEffect } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { AuthContext } from "context/Auth";
import { useAuth } from "hooks/useAuth";
import { dateLocale } from "i18n";
import capitalize from "../../utils/helpers/capitalize";
import {
  Alert,
  Box,
  Typography,
  Skeleton,
  Button,
  AlertTitle,
  Link,
} from "@mui/material";
import {
  // LocalHospitalRounded, // with the "Your Facility" section below
  BusinessRounded,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { SettingsRow, SettingsSection } from "./SettingsSection";
import DisplaySettings from "./DisplaySettings";
import DashboardPageHeader from "components/common/DashboardPageHeader";
// import AddressSettings from "./AddressSettings";
import AccountSettings from "./AccountSettings";
import PhoneSettings from "./PhoneSettings";
import PasswordSettings from "./PasswordSettings";
// import MarketingSettings from "./advertising/MarketingSettings";
import UsageDisplay from "./account/UsageDisplay";
import PlanManagement from "./account/PlanManagement";
import SeatManagementDialog from "./admin/SeatManagementDialog";
import DocumentProcessingSettings from "./admin/DocumentProcessingSettings";
import MfaEnrollDialog from "components/auth/MfaEnrollDialog";
import VisitStartSettingsDialog from "components/dashboard/visitStartCheckIns/VisitStartSettingsDialog";
import useMfaStatus from "hooks/useMfaStatus";
import { shouldSkipMfaEnrollmentUi } from "utils/isDevEnvironment";
import { LocationContext } from "context/Location";
import { formatDistanceToNow } from "date-fns";
import { getTrialLengthForRole } from "constants/trials";
import MorningReportBillingSettings from "features/morningReport/components/MorningReportBillingSettings";

// `label` is a key under settings.appearance.
/**
 * Account settings. Normally opened as a dialog from the sidebar's account menu
 * (`embedded`: the dialog supplies the title). The /dashboard/settings route still
 * renders it as a page for deep links like ?tab=phone.
 */
const Settings = ({ embedded = false }) => {
  const { user, userData, subscription } = useContext(AuthContext);
  const {
    hasActiveTrial,
    hasActiveDailyPass,
    hasValidSubscription,
    hasActiveSubscription,
    subscriptionTier,
    subscriptionData,
    isBetaAdmin,
    isGlobalAdmin,
    isRegionalAdmin,
  } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { location } = useContext(LocationContext);

  const [loading] = useState();
  const [submitted, setSubmitted] = useState();

  // const [addressOpen, setAddressOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  // const [marketingOpen, setMarketingOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [seatsOpen, setSeatsOpen] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [checkInsOpen, setCheckInsOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const { isEnrolled, hasTOTP, hasSMS, enrolledFactors } = useMfaStatus();

  // Admin billing info
  const seatCount = subscriptionData?.items?.[0]?.quantity || 0;
  const billingInterval =
    subscriptionData?.items?.[0]?.price?.recurring?.interval === "year"
      ? "yearly"
      : "monthly";
  const seatLabel = t("settings.billing.seatCount", { count: seatCount });

  // Handle URL parameters for auto-opening dialogs
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "phone") {
      setPhoneOpen(true);
      // Clear the parameter to avoid reopening on refresh
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handlePortalAction = async (flowType) => {
    setActionLoading(flowType);
    try {
      const createPortalSession = firebase
        .functions()
        .httpsCallable("createPortalSession");

      const { data } = await createPortalSession({
        successUrl: window.location.href,
        cancelUrl: window.location.href,
        role: userData?.role,
        stripeId: userData?.stripeId,
        flowType,
        subscriptionId: subscriptionData?.id,
      });

      if (data.url) {
        window.location.assign(data.url);
      }
    } catch (error) {
      console.error("Error opening portal:", error);
      alert(t("settings.billing.portalFailed"));
      setActionLoading(null);
    }
  };

  // getTrialLabelForRole's wording, in the app's language.
  const trialLabel = t("settings.trial.label", { count: getTrialLengthForRole(userData?.role) });
  const isFacility = userData?.role === "facility";
  const isAdmin = userData?.role === "admin";

  const showsConsumerBilling =
    !isFacility &&
    !isAdmin &&
    !userData?.betaAccess &&
    !isGlobalAdmin &&
    !isRegionalAdmin;

  const facilityTitle =
    userData?.role === "facility" && location?.title
      ? capitalize(location.title)
      : null;

  return (
    <Box>
      {/* In the dialog the title comes from DialogTitle, and the Email row already
          says who is signed in, so only a clinic's account name is worth adding. */}
      {embedded ? (
        facilityTitle && (
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            <Trans
              i18nKey="settings.signedInFacility"
              values={{ facility: facilityTitle }}
              components={{ 1: <strong /> }}
            />
          </Typography>
        )
      ) : (
        <DashboardPageHeader
          title={t("settings.title")}
          subtitle={
            <Typography>
              <Trans
                i18nKey={facilityTitle ? "settings.loggedInAsFacility" : "settings.loggedInAs"}
                values={{ who: user?.email || userData?.phone, facility: facilityTitle }}
                components={{ 1: <strong /> }}
              />
            </Typography>
          }
        />
      )}

      {submitted ? (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSubmitted(null)}
        >
          {submitted}
        </Alert>
      ) : null}

      {/* Daily Pass Status Display */}
      {hasActiveDailyPass && userData?.dailyPassExpiresAt && (
        <Alert severity="info" sx={{ pt: 1, mb: 3 }}>
          <Box>
            <AlertTitle>{t("settings.dailyPass.title")}</AlertTitle>
            {t("settings.dailyPass.expires", {
              tier: userData?.subscriptionTier || "Medium",
              when: formatDistanceToNow(new Date(userData.dailyPassExpiresAt), {
                addSuffix: true,
                locale: dateLocale(),
              }),
            })}
          </Box>
        </Alert>
      )}

      {/* Trial Status Display */}
      {hasActiveTrial && (
        <Alert severity="success" sx={{ pt: 1, mb: 3 }}>
          <Box>
            <AlertTitle>{t("settings.trial.title", { trial: trialLabel })}</AlertTitle>
            {isFacility ? t("settings.trial.facility") : t("settings.trial.individual")}
            <Button
              color="inherit"
              variant="outlined"
              onClick={() => navigate("/dashboard/upgrade")}
              sx={{ mt: 2, mb: 1, display: "block" }}
            >
              {t("common.upgrade")}
            </Button>
          </Box>
        </Alert>
      )}

      <DisplaySettings />

      <SettingsSection title={t("settings.account.title")}>
        <SettingsRow
          label={t("settings.account.email")}
          detail={user?.email || t("settings.account.noEmail")}
          action={
            <Button
              variant="outlined"
              size="small"
              onClick={() => setAccountOpen(true)}
            >
              {t("common.change")}
            </Button>
          }
        />
        <SettingsRow
          label={t("settings.account.password")}
          action={
            <Button
              variant="outlined"
              size="small"
              onClick={() => setPasswordOpen(true)}
            >
              {t("common.change")}
            </Button>
          }
        />
      </SettingsSection>

      {/* MFA enrollment UI omitted in dev/sandbox (sign-in MFA challenge unchanged) */}
      {!shouldSkipMfaEnrollmentUi() && (
        <SettingsSection title={t("settings.security.title")}>
          <SettingsRow
            label={t("settings.security.twoFactor")}
            detail={
              isEnrolled
                ? t(
                    hasTOTP
                      ? "settings.security.onAuthenticator"
                      : hasSMS
                        ? "settings.security.onSms"
                        : "settings.security.on",
                  )
                : t("settings.security.off")
            }
            action={
              <Button
                variant="outlined"
                size="small"
                onClick={() => setMfaOpen(true)}
              >
                {isEnrolled ? t("common.manage") : t("settings.security.setUp")}
              </Button>
            }
          />
        </SettingsSection>
      )}

      {/* Facility Info */}
      {userData?.role === "facility" && (
        <>
          {/* TODO: "Your Facility" hidden while the facility role is held
              back. It was the only way into AddressSettings and
              MarketingSettings, so both dialogs are commented out further down
              with it, along with the LocalHospitalRounded import and the
              open-state for both.

              Patient Check-ins below stays: it is the one facility surface
              still switched on, and a clinic needs to choose its questions.

          <InfoBox
            icon={
              <LocalHospitalRounded
                fontSize="large"
                sx={{ color: "#117aca" }}
              />
            }
            title="Your Facility"
            description="Manage your facility location, group settings for multi-location maps, and advertising profile."
            links={[
              {
                title: "Manage Facility & Location",
                onClick: () => setAddressOpen(true),
              },
              {
                title: "Update Your Advertising Profile",
                onClick: () => setMarketingOpen(true),
              },
            ]}
          />
          */}

          {/* Which questions the clinic's check-in asks. It lives here rather
              than on the Check-ins page itself because it is configuration a
              clinic touches about twice, and the page it belongs to is a working
              inbox the front desk reads all day. The page links here. */}
          <SettingsSection title={t("settings.checkIns.title")}>
            <SettingsRow
              label={t("settings.checkIns.questions")}
              detail={t("settings.checkIns.questionsDetail")}
              action={
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCheckInsOpen(true)}
                >
                  {t("common.edit")}
                </Button>
              }
            />
          </SettingsSection>

          {/* Multi-Location Map URL Alert - Only show if group name is set */}
          {location?.group && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {t("settings.groupMap", { group: location.group })}{" "}
              <Link
                href={`${window.location.origin}/map/${location.group
                  .toLowerCase()
                  .replace(/[^a-z0-9\s\-'&]/g, "")
                  .replace(/[\s'&]+/g, "-")
                  .replace(/-+/g, "-")
                  .replace(/^-|-$/g, "")}`}
                target="_blank"
                variant="body2"
              >
                {window.location.origin}/map/
                {location.group
                  .toLowerCase()
                  .replace(/[^a-z0-9\s\-'&]/g, "")
                  .replace(/[\s'&]+/g, "-")
                  .replace(/-+/g, "-")
                  .replace(/^-|-$/g, "")}
              </Link>
              .
            </Alert>
          )}

          {/* Plan Management for facilities - Only show for users with REAL subscription (not daily pass or trial) */}
          {hasValidSubscription && !hasActiveTrial && !hasActiveDailyPass && (
            <PlanManagement
              subscriptionTier={subscriptionTier}
              loading={loading}
            />
          )}
        </>
      )}

      {/* MorningReport billing: learners only (it's off for professionals for now). */}
      {userData?.role === "learner" && (
        <MorningReportBillingSettings />
      )}

      {/* Usage and plan are consumer billing. Clinics, org admins, beta users and
          platform admins don't have it. */}
      {showsConsumerBilling && (
        <UsageDisplay userData={userData} subscription={subscription} />
      )}

      {/* Only for a REAL subscription (not a daily pass or trial). */}
      {showsConsumerBilling &&
        hasValidSubscription &&
        !hasActiveTrial &&
        !hasActiveDailyPass && (
          <PlanManagement
            subscriptionTier={subscriptionTier}
            loading={loading}
          />
        )}

      {/* Admin Billing Management — [BETA-KIJABE-ADMIN] hidden for beta admin orgs (no Stripe subscription) */}
      {isAdmin && hasActiveSubscription && !isBetaAdmin && (
        <SettingsSection title={t("settings.billing.title")}>
          {subscriptionData?.cancel_at_period_end && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {t("settings.billing.cancelScheduled")}
            </Alert>
          )}
          {!subscriptionData?.cancel_at_period_end && (
            <SettingsRow
              label={t("settings.billing.seats")}
              detail={t(
                billingInterval === "yearly"
                  ? "settings.billing.seatsDetailYearly"
                  : "settings.billing.seatsDetailMonthly",
                { seats: seatLabel },
              )}
              action={
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSeatsOpen(true)}
                >
                  {t("common.manage")}
                </Button>
              }
            />
          )}
          <SettingsRow
            label={t("settings.billing.paymentMethod")}
            action={
              <LoadingButton
                variant="outlined"
                size="small"
                loading={actionLoading === "payment_method_update"}
                disabled={!!actionLoading}
                onClick={() => handlePortalAction("payment_method_update")}
              >
                {subscriptionData?.cancel_at_period_end ? t("common.renew") : t("common.change")}
              </LoadingButton>
            }
          />
          {!subscriptionData?.cancel_at_period_end && (
            <SettingsRow
              label={t("settings.billing.subscription")}
              detail={t("settings.billing.subscriptionDetail")}
              action={
                <LoadingButton
                  variant="outlined"
                  size="small"
                  color="error"
                  loading={actionLoading === "subscription_cancel"}
                  disabled={!!actionLoading}
                  onClick={() => handlePortalAction("subscription_cancel")}
                >
                  {t("common.cancel")}
                </LoadingButton>
              }
            />
          )}
        </SettingsSection>
      )}

      {/* Document processing backend (org / global admins only; self-hides otherwise) */}
      <DocumentProcessingSettings />

      {/* Settings Dialogs */}
      {/* Unreachable without the "Your Facility" section above, which was the
          only thing that opened either. Their imports and open-state are
          commented out at the top of this file. Restore all four together.

      <AddressSettings
        user={user}
        data={userData}
        location={location}
        visible={addressOpen}
        close={() => setAddressOpen(false)}
        setSubmitted={setSubmitted}
      />

      <MarketingSettings
        user={user}
        data={userData}
        location={location}
        visible={marketingOpen}
        close={() => setMarketingOpen(false)}
        setSubmitted={setSubmitted}
      />
      */}

      <AccountSettings
        user={user}
        userData={userData}
        visible={accountOpen}
        close={() => setAccountOpen(false)}
        setSubmitted={setSubmitted}
      />

      <PhoneSettings
        user={user}
        userData={userData}
        visible={phoneOpen}
        close={() => setPhoneOpen(false)}
        setSubmitted={setSubmitted}
      />

      <PasswordSettings
        user={user}
        visible={passwordOpen}
        close={() => setPasswordOpen(false)}
        setSubmitted={setSubmitted}
      />

      <VisitStartSettingsDialog
        open={checkInsOpen}
        onClose={() => setCheckInsOpen(false)}
        clinicId={location?.id ? String(location.id) : null}
        clinicName={
          location?.title ? capitalize(location.title) : t("settings.checkIns.yourClinic")
        }
        onSaved={() => setSubmitted(t("settings.checkIns.saved"))}
      />

      {/* Admin Seat Management Dialog */}
      <SeatManagementDialog
        open={seatsOpen}
        onClose={() => setSeatsOpen(false)}
      />

      {!shouldSkipMfaEnrollmentUi() && (
        <MfaEnrollDialog
          open={mfaOpen}
          source="settings"
          enrolledFactors={enrolledFactors}
          onClose={() => setMfaOpen(false)}
          onEnrolled={() => {
            setMfaOpen(false);
            setSubmitted(t("settings.security.enabled"));
          }}
        />
      )}
    </Box>
  );
};

export default Settings;
